import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CollectionPage from '../shared/CollectionPage';
import gameEditFields from './gameEditFields';

/**
 * Game Collection Page component
 * Uses the generic CollectionPage with game-specific configuration
 */
const GAME_PLATFORMS = [
  'PC', 'PlayStation 5', 'PlayStation 4', 'PlayStation 3', 'PlayStation 2', 'PlayStation',
  'Xbox Series X/S', 'Xbox One', 'Xbox 360', 'Xbox',
  'Nintendo Switch', 'Wii U', 'Wii', 'GameCube', 'Nintendo 64', 'Super Nintendo', 'NES',
  'Nintendo 3DS', 'Nintendo DS', 'Game Boy Advance', 'Game Boy Color', 'Game Boy',
  'PS Vita', 'PSP',
  'Sega Mega Drive', 'Sega Genesis', 'Sega Saturn', 'Sega Dreamcast',
  'Sega Master System', 'Game Gear',
  'Amiga', 'Atari 2600', 'Neo Geo',
  'Other',
];

const GameCollectionPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const gameFilterOptions = [
    { value: '', label: 'All Platforms' },
    ...GAME_PLATFORMS.map(p => ({ value: p, label: p })),
  ];

  // Game-specific render props
  const gameRenderProps = {
    getItemSubtitle: (item) => {
      const parts = [item.platform];
      if (item.genre) parts.push(item.genre);
      if (item.releaseYear) parts.push(item.releaseYear);
      return parts.join(' · ');
    },
    getItemDetails: (item) => (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {item.developer && (
          <Typography variant="body2" color="text.secondary">
            Dev: {item.developer}
          </Typography>
        )}
        {item.publisher && item.publisher !== item.developer && (
          <Typography variant="body2" color="text.secondary">
            Pub: {item.publisher}
          </Typography>
        )}
        {item.region && (
          <Typography variant="body2" color="text.secondary">
            Region: {item.region}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          Priority: {item.priority || 'Medium'}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', rowGap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Played:</Typography>
            {item.played ? (
              <CheckCircleIcon color="success" fontSize="small" />
            ) : (
              <CancelIcon color="error" fontSize="small" />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Completed:</Typography>
            {item.completed ? (
              <EmojiEventsIcon sx={{ color: 'gold' }} fontSize="small" />
            ) : (
              <CancelIcon color="error" fontSize="small" />
            )}
          </Box>
        </Box>
      </Box>
    )
  };

  // Game-specific filter function
  const getGameFilterValue = (item) => item.platform;


  return (
    <CollectionPage
      collectionType="games"
      title="Game Collection"
      renderProps={gameRenderProps}
      filterOptions={gameFilterOptions}
      getFilterValue={getGameFilterValue}
      editFields={gameEditFields}
      itemType="game"
    />
  );
};

export default GameCollectionPage;
